import { useCallback, useEffect, useState } from 'react'
import {
	Button,
	Card,
	Checkbox,
	Col,
	DatePicker,
	Input,
	Modal,
	message,
	Row,
	Select,
	Space,
	Tag,
	Tooltip,
	Typography,
} from 'antd'
import api from '../../api/index.api'
import { FileSearchOutlined } from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import {
	ListPageIntro,
	ListPageShell,
	ListPageTotal,
	PagedResourceList,
} from '../../common'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { onGetReports } from '../../redux/report/report.action'
import {
	formatReportSource,
	getReportRecordWordLemma,
} from '../../utils/feedback.utils'
import { formatDateTime } from '../../utils/function.utils'
import { DEFAULT_PAGE_SIZE, ROUTE } from '../../utils/constant'
import { ReportWordLangPieCharts } from './ReportWordLangPieCharts'
import './ReportList.scss'

const { RangePicker } = DatePicker
const { TextArea } = Input
const { Text } = Typography

const REPORT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const DEFAULT_RESOLVED_NOTIFICATION_MESSAGE =
	'The word definition that you reported has been fixed. You can try to translate it again. Thanks for your patience.'

const buildDefaultResolvedNotificationMessage = record => {
	const word = getReportRecordWordLemma(record)
	if (!word) {
		return DEFAULT_RESOLVED_NOTIFICATION_MESSAGE
	}
	return `The word '${word}' that you reported has been fixed. You can try to translate it again. Thanks for your patience.`
}

const REPORT_TYPE = {
	NO_DEF: 'no_definition',
	REPORT: 'report',
}

const formatReportType = value => {
	switch (value) {
		case REPORT_TYPE.NO_DEF:
			return <Tag color="magenta">No definition</Tag>
		case REPORT_TYPE.REPORT:
			return <Tag color="orange">Report</Tag>
		default:
			return <Tag>{value}</Tag>
	}
}

const getReportedWordFromCreatedAt = source => {
	const texts = source.split(',')
	if (texts.length >= 2) {
		return texts[0]
	}
	return ''
}

const getSourceFromCreatedAt = source => {
	const texts = source.split(',')
	if (texts.length >= 2) {
		return texts[1]
	}
	return source
}

const buildQueryFilter = (onlyNoDefinition, dateRange, wordLang) => {
	const f = {}
	if (onlyNoDefinition) {
		f.rating = REPORT_TYPE.NO_DEF
	}
	if (dateRange && dateRange[0] && dateRange[1]) {
		f.createdAtFrom = dateRange[0].clone().startOf('day').toISOString()
		f.createdAtTo = dateRange[1].clone().endOf('day').toISOString()
	}
	if (wordLang === 'en' || wordLang === 'vi') {
		f.wordLang = wordLang
	}
	return f
}

const isUnauthenticatedReport = record => {
	const email = String(record?.email ?? '')
		.trim()
		.toLowerCase()
	const name = String(record?.name ?? '')
		.trim()
		.toLowerCase()
	return email === 'unauthenticated' || name === 'unauthenticated'
}

const isReportResolved = record =>
	String(record?.status ?? '')
		.trim()
		.toLowerCase() === 'resolved'

export const ReportList = () => {
	const dispatch = useAppDispatch()
	const history = useHistory()
	const report = useAppSelector(state => state.report)

	const [onlyNoDefinition, setOnlyNoDefinition] = useState(true)
	const [dateRange, setDateRange] = useState(null)
	const [wordLang, setWordLang] = useState(undefined)
	const [queryFilter, setQueryFilter] = useState(() =>
		buildQueryFilter(true, null, undefined)
	)
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
	const [resolveModalOpen, setResolveModalOpen] = useState(false)
	const [resolveRecord, setResolveRecord] = useState(null)
	const [resolveNotificationDraft, setResolveNotificationDraft] =
		useState('')
	const [resolveSendEmail, setResolveSendEmail] = useState(false)
	const [reportStats, setReportStats] = useState(null)
	const [statsLoading, setStatsLoading] = useState(false)

	const fetchPage = useCallback(
		(page, cb) =>
			dispatch(onGetReports(page, { ...queryFilter, pageSize }, cb)),
		[dispatch, queryFilter, pageSize]
	)

	useEffect(() => {
		dispatch(onGetReports(0, { ...queryFilter, pageSize }))
	}, [dispatch, queryFilter, pageSize])

	const fetchReportStats = useCallback(
		async (options = {}) => {
			const { showSpinner = true } = options
			if (showSpinner) {
				setStatsLoading(true)
			}
			try {
				const params = {}
				if (queryFilter.createdAtFrom) {
					params.createdAtFrom = queryFilter.createdAtFrom
				}
				if (queryFilter.createdAtTo) {
					params.createdAtTo = queryFilter.createdAtTo
				}
				const data = await api.reportApi.getReportStats(params)
				setReportStats(data)
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error(e)
				setReportStats(null)
			} finally {
				if (showSpinner) {
					setStatsLoading(false)
				}
			}
		},
		[queryFilter.createdAtFrom, queryFilter.createdAtTo]
	)

	useEffect(() => {
		fetchReportStats({ showSpinner: true })
	}, [fetchReportStats])

	const applyFilters = () => {
		setQueryFilter(buildQueryFilter(onlyNoDefinition, dateRange, wordLang))
	}

	const resetFilters = () => {
		setDateRange(null)
		setOnlyNoDefinition(true)
		setWordLang(undefined)
		setQueryFilter(buildQueryFilter(true, null, undefined))
	}

	const closeResolveModal = useCallback(() => {
		setResolveModalOpen(false)
		setResolveRecord(null)
		setResolveNotificationDraft('')
		setResolveSendEmail(false)
	}, [])

	const openResolveModal = useCallback(record => {
		setResolveRecord(record)
		setResolveNotificationDraft('')
		setResolveModalOpen(true)
	}, [])

	const handleResolveOk = useCallback(async () => {
		if (resolveRecord == null) return
		const defaultNotificationMessage =
			buildDefaultResolvedNotificationMessage(resolveRecord)
		const notificationMessage = resolveNotificationDraft.trim()
			? resolveNotificationDraft.trim()
			: defaultNotificationMessage
		try {
			await api.reportApi.resolveReport(
				resolveRecord.id,
				notificationMessage,
				resolveSendEmail
			)
			message.success(
				resolveSendEmail
					? 'Report marked resolved. The reporter was notified in-app and by email.'
					: 'Report marked resolved and the user was notified.'
			)
			closeResolveModal()
			const pageIndex = Math.max(0, (report?.page ?? 1) - 1)
			fetchPage(pageIndex)
			await fetchReportStats({ showSpinner: false })
		} catch (err) {
			message.error(
				err instanceof Error ? err.message : 'Failed to resolve report'
			)
			throw err
		}
	}, [
		closeResolveModal,
		fetchPage,
		fetchReportStats,
		report?.page,
		resolveNotificationDraft,
		resolveRecord,
		resolveSendEmail,
	])

	const openUpdateDefinition = useCallback(
		record => {
			if (record == null) return
			const lemma = getReportRecordWordLemma(record)
			if (!lemma) return
			const qs = new URLSearchParams({ word: lemma }).toString()
			history.push(`${ROUTE.DICTIONARY_MANAGEMENT}${ROUTE.LIST}?${qs}`)
		},
		[history]
	)

	const columns = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 72,
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
			width: 160,
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			width: 140,
		},
		{
			title: 'Total',
			dataIndex: 'userReportTotal',
			key: 'userReportTotal',
			width: 72,
			align: 'center',
			render: v =>
				v != null && v !== '' ? (
					<span>{v}</span>
				) : (
					<span className="report-list__muted">—</span>
				),
		},
		{
			title: 'Word',
			dataIndex: 'content',
			key: 'word',
			align: 'center',
			width: 140,
			// eslint-disable-next-line react/display-name
			render: (_, record) => {
				let word = ''
				if (record.rating === REPORT_TYPE.NO_DEF) {
					word = record.content
				} else {
					word = getReportedWordFromCreatedAt(record.createdBy)
				}

				return (
					<code className="report-list__word">{word || '—'}</code>
				)
			},
		},
		{
			title: 'Context',
			dataIndex: 'context',
			key: 'context',
			width: 360,
			ellipsis: true,
			render: value => {
				const context = typeof value === 'string' ? value.trim() : ''
				if (!context) {
					return <span className="report-list__muted">—</span>
				}
				return (
					<Tooltip
						title={context}
						overlayClassName="report-list__context-tooltip"
					>
						<span className="report-list__context-cell">
							{context}
						</span>
					</Tooltip>
				)
			},
		},
		{
			title: 'Content',
			dataIndex: 'content',
			key: 'content',
			width: 220,
			render: (data, record) => {
				if (record.rating === REPORT_TYPE.NO_DEF) {
					return <span className="report-list__muted">—</span>
				}
				return data
			},
		},
		{
			title: 'Type',
			dataIndex: 'rating',
			key: 'rating',
			width: 130,
			render: data => formatReportType(data),
		},
		{
			title: 'Reported at',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 168,
			render: data => formatDateTime(data),
		},
		{
			title: 'Source',
			dataIndex: 'createdBy',
			key: 'createdBy',
			width: 120,
			render: data => formatReportSource(getSourceFromCreatedAt(data)),
		},
		{
			title: 'Action',
			key: 'action',
			width: 200,
			align: 'center',
			// eslint-disable-next-line react/display-name
			render: (_, record) => {
				const unauthenticated = isUnauthenticatedReport(record)
				const resolved = isReportResolved(record)
				const resolvedButtonDisabled = unauthenticated || resolved
				const resolvedTooltip = unauthenticated
					? 'Reports from unauthenticated users cannot be marked resolved.'
					: resolved
						? 'This report was marked resolved.'
						: 'Mark this translation as fixed and notify users about the update'
				const resolvedButton = (
					<Button
						className={
							resolved ? 'report-list__resolved-btn' : undefined
						}
						disabled={resolvedButtonDisabled}
						onClick={
							resolvedButtonDisabled
								? undefined
								: () => openResolveModal(record)
						}
					>
						Resolved
					</Button>
				)
				const updateDefTooltip =
					'Update definition for this reported word'
				const updateDefButton = (
					<Button
						onClick={() => openUpdateDefinition(record)}
					>
						Update definition
					</Button>
				)
				return (
					<div className="report-list__action-cell">
						<Space
							direction="vertical"
							size={8}
							className="report-list__action-buttons"
						>
							<Tooltip title={updateDefTooltip}>
								<span className="report-list__action-btn-wrap">
									{updateDefButton}
								</span>
							</Tooltip>
							<Tooltip
								overlayClassName="report-list__resolved-tooltip"
								title={resolvedTooltip}
							>
								<span
									className={`report-list__action-btn-wrap${
										resolvedButtonDisabled
											? ' report-list__action-btn-wrap--inactive'
											: ''
									}`}
								>
									{resolvedButton}
								</span>
							</Tooltip>
						</Space>
					</div>
				)
			},
		},
	]

	const getRowKey = record => record.id

	const topLeft = (
		<ListPageIntro
			icon={FileSearchOutlined}
			title="User reports"
			subtitle='Review reports submitted from the app. By default only "no definition" is shown. Use the date range to filter by when the report was sent.'
		>
			<Card className="list-page-toolbar-card" size="small" bordered>
				<Row gutter={[16, 16]} align="middle">
					<Col xs={24} lg={6}>
						<Checkbox
							checked={onlyNoDefinition}
							onChange={e =>
								setOnlyNoDefinition(e.target.checked)
							}
						>
							Only &quot;no definition&quot; reports
						</Checkbox>
					</Col>
					<Col xs={24} lg={6}>
						<div className="report-list__field-label">
							Word column language
						</div>
						<Select
							allowClear
							placeholder="All languages"
							value={wordLang}
							onChange={v => setWordLang(v)}
							className="report-list__word-lang"
						>
							<Select.Option value="en">English (en)</Select.Option>
							<Select.Option value="vi">
								Vietnamese (vi)
							</Select.Option>
						</Select>
					</Col>
					<Col xs={24} lg={8}>
						<div className="report-list__field-label">
							Report date range
						</div>
						<RangePicker
							value={dateRange}
							onChange={dates => setDateRange(dates)}
							format="YYYY-MM-DD"
							allowClear
							className="report-list__range"
						/>
					</Col>
					<Col xs={24} lg={4}>
						<Space wrap>
							<Button type="primary" onClick={applyFilters}>
								Apply filters
							</Button>
							<Button onClick={resetFilters}>Reset</Button>
						</Space>
					</Col>
				</Row>
			</Card>
			<Card
				size="small"
				bordered
				className="report-list__charts-card"
				title="Reports by word language"
			>
				<Text type="secondary" className="report-list__charts-intro">
					<strong>Missing definition</strong> counts open{' '}
					<code>no_definition</code> reports (not yet resolved).{' '}
					<strong>Definition added</strong> counts{' '}
					<code>report</code> rows plus <code>no_definition</code>{' '}
					rows you marked resolved. Word-column language parsing
					matches the table. The report date range above applies; the
					word-language dropdown and &quot;only no definition&quot;
					checkbox do not change these charts.
				</Text>
				<ReportWordLangPieCharts
					stats={reportStats}
					loading={statsLoading}
				/>
			</Card>
		</ListPageIntro>
	)

	const topRight = <ListPageTotal total={report?.total} />

	return (
		<ListPageShell>
			<PagedResourceList
				resource={report}
				columns={columns}
				fetchPage={fetchPage}
				rowKey={getRowKey}
				headerClassName="list-page-bar"
				topLeft={topLeft}
				topRight={topRight}
				tableProps={{
					scroll: { x: 1900 },
				}}
				fetchOnMount={false}
				pageSize={pageSize}
				onPageSizeChange={setPageSize}
				showSizeChanger
				pageSizeOptions={REPORT_PAGE_SIZE_OPTIONS.map(String)}
			/>
			<Modal
				title="Mark this report as resolved?"
				visible={resolveModalOpen}
				okText="Resolve"
				cancelText="Cancel"
				maskClosable
				onOk={handleResolveOk}
				onCancel={closeResolveModal}
			>
				<div className="report-list__resolve-modal">
					<p className="report-list__resolve-modal-intro">
						This will mark the translation as fixed and notify the
						reporter.
					</p>
					<div className="report-list__field-label">
						Notification message
					</div>
					<TextArea
						rows={4}
						value={resolveNotificationDraft}
						onChange={e =>
							setResolveNotificationDraft(e.target.value)
						}
						placeholder={buildDefaultResolvedNotificationMessage(
							resolveRecord
						)}
					/>
					<Text
						type="secondary"
						className="report-list__resolve-modal-hint"
					>
						Leave blank to use the default message shown as the
						placeholder above.
					</Text>
					<Checkbox
						className="report-list__resolve-send-email"
						checked={resolveSendEmail}
						onChange={e =>
							setResolveSendEmail(e.target.checked)
						}
					>
						Also send this message by email to{' '}
						<strong>{resolveRecord?.email ?? '—'}</strong>
					</Checkbox>
				</div>
			</Modal>
		</ListPageShell>
	)
}
